import { useEffect, useState } from 'react';
import { Card, Button, List, Typography, Space, Tag, Alert, Empty, Divider } from 'antd';
import { CheckCircleFilled, ExperimentOutlined, ExclamationCircleOutlined, UserOutlined, MailOutlined } from '@ant-design/icons';
import Spinner from '../spinner/Spinner';
import HorizontalLoader from '../HorizontalLoader/HorizontalLoader';
import useModalDetails from '../../hooks/useModalDetails';
import Modal from '../Modal/Modal';
import { t } from '../../i18n/i18n';
import { TestGroupTypes } from '../../constants/enums';
import './ResetTestgroupStep4.scss';

const { Title, Text } = Typography;

const ResetTestgroupStep4 = ({
  loading,
  handlePreviousStep,
  handleNextStep,
  selectedTestgroups,
  clearSelectedTestgroups,
  handleSelectTestgroup,
  selectedInvitations,
  fetchSearchScreening,
  testgroups,
  restartPrefilters,
}) => {
  const { showModal, closeModal, handleShowModal } = useModalDetails();
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    clearSelectedTestgroups();
    fetchSearchScreening();
  }, []);

  return (
    <>
      <Modal
        show={showModal}
        relativeCloseButton
        className="px-4 pt-2 pb-3 w-50"
        closeBtnClassname="text-blue-principal font-weight-bold"
        setShow={(e) => closeModal(e)}
      >
        <div className="modern-confirmation-modal">
          <Alert
            message={t('RESET_TESTGROUP_STEP_4_MODAL_WARNING')}
            type="error"
            icon={<ExclamationCircleOutlined />}
            className="mb-4"
            showIcon
          />
          
          <Card className="confirmation-details-card" bordered={false}>
            <Space direction="vertical" size="large" className="w-100">
              <div>
                <Title level={5} className="mb-3">
                  <ExperimentOutlined className="mr-2" />
                  {t('RESET_TESTGROUP_STEP_4_MODAL_FIRST_ITEM')}
                </Title>
                <List
                  size="small"
                  dataSource={testgroups.filter((item) => selectedTestgroups.includes(item.id))}
                  renderItem={(item) => (
                    <List.Item className="py-2">
                      <Tag color={item.type !== TestGroupTypes.PRE_FILTER ? "blue" : "orange"} className="confirmation-tag">
                        {item.type !== TestGroupTypes.PRE_FILTER
                          ? item.name
                          : t('TESTGROUP_PRE_FILTER_TITLE_NAME')}
                      </Tag>
                    </List.Item>
                  )}
                />
              </div>
              
              <Divider className="my-2" />
              
              <div>
                <Title level={5} className="mb-3">
                  <UserOutlined className="mr-2" />
                  {t('RESET_TESTGROUP_STEP_4_MODAL_SECOND_ITEM')}
                </Title>
                <List
                  size="small"
                  dataSource={selectedInvitations}
                  renderItem={(item) => (
                    <List.Item className="py-2">
                      <Card size="small" className="candidate-confirmation-card">
                        <Space>
                          <MailOutlined style={{ color: '#4987BE' }} />
                          <Text strong>{item.email}</Text>
                        </Space>
                      </Card>
                    </List.Item>
                  )}
                />
              </div>
            </Space>
            
            <div className="confirmation-footer">
              {isProcessing ? (
                <div className="processing-container">
                  <HorizontalLoader className="processing-loader" />
                </div>
              ) : (
                <Space>
                  <Button onClick={() => closeModal()} className="cancel-confirmation-btn">
                    {t('RESET_TESTGROUP_BACK_BUTTON')}
                  </Button>
                  <Button
                    type="primary"
                    danger
                    onClick={async () => {
                      setIsProcessing(true);
                      await handleNextStep();
                      closeModal();
                      setIsProcessing(false);
                    }}
                    className="confirm-action-btn"
                  >
                    {t('RESET_TESTGROUP_STEP_4_MODAL_ACCEPT_BUTTON')}
                  </Button>
                </Space>
              )}
            </div>
          </Card>
        </div>
      </Modal>
      <div className="reset-step-container modern-step">
        <div className="step-header">
          <Title level={3} className="step-title">
            <ExperimentOutlined className="step-icon" />
            {t('RESET_TESTGROUP_STEP_4_TITLE')}
          </Title>
        </div>

        <div className="step-content">
          <div className="testgroups-selection-area">
            {loading ? (
              <div className="loading-container">
                <Spinner show />
              </div>
            ) : (
              <>
                {!restartPrefilters && (
                  <List
                    className="testgroups-list"
                    dataSource={testgroups?.filter((item) => item.type !== TestGroupTypes.PRE_FILTER)}
                    renderItem={(testgroup) => (
                      <List.Item className="testgroup-item">
                        <Card
                          hoverable
                          onClick={() => handleSelectTestgroup(testgroup.id)}
                          className={`testgroup-card ${selectedTestgroups.includes(testgroup.id) ? 'selected-card' : ''}`}
                          size="small"
                        >
                          <div className="testgroup-content">
                            <Space className="w-100 justify-content-between align-items-center">
                              <div className="testgroup-info">
                                <Text strong className="testgroup-name">{testgroup.name}</Text>
                              </div>
                              {selectedTestgroups.includes(testgroup.id) && (
                                <CheckCircleFilled className="check-icon" />
                              )}
                            </Space>
                          </div>
                        </Card>
                      </List.Item>
                    )}
                  />
                )}

                {restartPrefilters && (
                  <List
                    className="testgroups-list"
                    dataSource={testgroups?.filter((item) => item.type === TestGroupTypes.PRE_FILTER)}
                    renderItem={(testgroup) => (
                      <List.Item className="testgroup-item">
                        <Card
                          hoverable
                          onClick={() => handleSelectTestgroup(testgroup.id)}
                          className={`testgroup-card prefilter ${selectedTestgroups.includes(testgroup.id) ? 'selected-card' : ''}`}
                          size="small"
                        >
                          <div className="testgroup-content">
                            <Space className="w-100 justify-content-between align-items-center">
                              <div className="testgroup-info">
                                <Text strong className="testgroup-name">
                                  {t('TESTGROUP_PRE_FILTER_TITLE_NAME')}
                                </Text>
                                <br />
                                <Tag size="small" color="orange">{t('PREFILTER_TAG_LABEL')}</Tag>
                              </div>
                              {selectedTestgroups.includes(testgroup.id) && (
                                <CheckCircleFilled className="check-icon" />
                              )}
                            </Space>
                          </div>
                        </Card>
                      </List.Item>
                    )}
                  />
                )}

                {((testgroups?.filter((item) => !restartPrefilters ? item.type !== TestGroupTypes.PRE_FILTER : item.type === TestGroupTypes.PRE_FILTER)).length === 0) && (
                  <Empty
                    description={t('NO_TESTGROUPS_FOUND')}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    className="empty-state"
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <div className="step-footer">
        <Space>
          <Button onClick={handlePreviousStep} className="back-btn" size="large">
            {t('RESET_TESTGROUP_BACK_BUTTON')}
          </Button>
          <Button
            type="primary"
            danger
            onClick={handleShowModal}
            disabled={!selectedTestgroups.length}
            className="continue-btn"
            size="large"
          >
            {t('RESET_TESTGROUP_NEXT_BUTTON')} ({selectedTestgroups.length})
          </Button>
        </Space>
      </div>
    </>
  );
};

export default ResetTestgroupStep4;
